import React, { useCallback, useEffect, useState } from 'react';
import { Brain, ThumbsUp, ThumbsDown, ArrowsClockwise, Trash, CheckCircle } from '@phosphor-icons/react';

/*
  LearnedProfileCard
  ------------------
  Layer-3 of the self-learning system. Shows what the aggregator has learned
  about the family (skipped dishes, favourites, weekday elaboration, etc.)
  and lets the user reinforce (keep) or dismiss (forget) each learning.

  Props:
    api  — axios instance with baseURL /api

  Endpoints:
    GET  /meals/learned-profile
    POST /meals/learned-profile/feedback  { learning_id, action: 'keep'|'forget' }
    POST /meals/learned-profile/run-now   (admin) — force an aggregation now
    POST /meals/learned-profile/reset     (admin) — wipe everything
*/

const KIND_LABELS = {
  negative_dish: { icon: '🚫', label: 'Auto-avoid', color: 'red' },
  positive_dish: { icon: '⭐', label: 'Favourite', color: 'emerald' },
  avoid_forever: { icon: '⛔', label: 'Never suggest', color: 'red' },
  dow_pattern: { icon: '📅', label: 'Weekday pattern', color: 'purple' },
  chip_habit: { icon: '💡', label: 'Style preference', color: 'amber' },
  customization: { icon: '🌶️', label: 'Dish tweak', color: 'amber' },
  feedback: { icon: '💬', label: 'Family feedback', color: 'purple' },
};

function LearningRow({ L, onFeedback, busy }) {
  const meta = KIND_LABELS[L.kind] || { icon: '🧠', label: 'Insight', color: 'neutral' };
  const isForget = L.forgotten;
  return (
    <div
      className={`flex items-start justify-between gap-2 rounded-xl p-3 transition-colors ${isForget ? 'opacity-40' : ''}`}
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
      data-testid={`learning-row-${L.learning_id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm">{meta.icon}</span>
          <span className={`text-[9px] uppercase tracking-wider font-bold text-${meta.color}-300`}>{meta.label}</span>
          {L.applied && (
            <span className="text-[9px] font-bold text-emerald-300 inline-flex items-center gap-0.5">
              <CheckCircle size={10} weight="fill" /> applied
            </span>
          )}
          {L.feedback === 'keep' && (
            <span className="text-[9px] font-bold text-purple-300">✓ kept by you</span>
          )}
        </div>
        <p className="text-[13px] font-semibold text-white mt-0.5">{L.subject}</p>
        <p className="text-[11px] text-neutral-400 mt-0.5">{L.proposed_action}</p>
        <p className="text-[10px] text-neutral-500 mt-1 italic">Because: {L.evidence}</p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          disabled={busy || L.feedback === 'keep'}
          onClick={() => onFeedback(L.learning_id, 'keep')}
          data-testid={`learning-keep-${L.learning_id}`}
          title="Keep this — reinforce"
          className={`p-1.5 rounded-lg transition-colors ${L.feedback === 'keep'
            ? 'bg-purple-500/30 text-purple-200 cursor-default'
            : 'bg-white/5 text-neutral-400 hover:bg-purple-500/20 hover:text-purple-200'}`}
        >
          <ThumbsUp size={12} weight={L.feedback === 'keep' ? 'fill' : 'regular'} />
        </button>
        <button
          type="button"
          disabled={busy || L.forgotten}
          onClick={() => onFeedback(L.learning_id, 'forget')}
          data-testid={`learning-forget-${L.learning_id}`}
          title="Forget this — I don't agree"
          className={`p-1.5 rounded-lg transition-colors ${L.forgotten
            ? 'bg-red-500/30 text-red-200 cursor-default'
            : 'bg-white/5 text-neutral-400 hover:bg-red-500/20 hover:text-red-200'}`}
        >
          <ThumbsDown size={12} weight={L.forgotten ? 'fill' : 'regular'} />
        </button>
      </div>
    </div>
  );
}

export default function LearnedProfileCard({ api }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const r = await api.get('/meals/learned-profile');
      setData(r.data || {});
    } catch (e) { console.warn(e); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFeedback = async (learning_id, action) => {
    setBusy(true);
    try {
      await api.post('/meals/learned-profile/feedback', { learning_id, action });
      await fetchData();
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed to save feedback.');
    }
    setBusy(false);
  };

  const handleRunNow = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/meals/learned-profile/run-now');
      const s = r.data || {};
      window.alert(`Aggregation ran.\nSignals: ${s.signals_considered}\nLearnings: ${s.learnings_generated}\nApplied: ${s.learnings_applied}`);
      await fetchData();
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Aggregator run failed. (admin only)');
    }
    setRefreshing(false);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset ALL learning?\nThis clears all captured signals and forgets everything the AI learned. Your food profile (basics, allergies, favourites you set yourself) is kept.\n\nAlso revert auto-applied dish preferences?')) return;
    const revertPrefs = window.confirm('Revert auto-applied dish preferences too?\n\nOK = revert the auto-set dish likes/dislikes/nevers.\nCancel = keep them but stop learning further.');
    setBusy(true);
    try {
      const r = await api.post('/meals/learned-profile/reset', { revert_prefs: revertPrefs });
      window.alert(`Reset done. Signals deleted: ${r.data?.signals_deleted || 0}`);
      await fetchData();
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Reset failed. (admin only)');
    }
    setBusy(false);
  };

  if (!data) return null;

  const learnings = (data.learnings || []).filter(L => !L.forgotten || expanded);
  const applied = learnings.filter(L => L.applied);
  const observations = learnings.filter(L => !L.applied);
  const hasAny = (data.learnings || []).length > 0;
  const stats = data.stats || {};

  return (
    <div
      className="rounded-2xl p-4"
      style={{
        background: 'linear-gradient(135deg, rgba(168,85,247,0.05), rgba(255,107,53,0.03))',
        border: '1px solid rgba(168,85,247,0.15)',
      }}
      data-testid="learned-profile-card"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Brain size={18} weight="fill" className="text-purple-300 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-white">What the AI has learned about you</p>
            <p className="text-[10px] text-neutral-400 truncate">
              {data.learned_at
                ? `Updated ${new Date(data.learned_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                : 'Not yet learned — cook, rate & plan more to help the AI'}
              {' · '}{stats.total_signals || 0} observations
              {stats.unprocessed_signals > 0 && ` (${stats.unprocessed_signals} new)`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleRunNow}
            disabled={refreshing}
            data-testid="learned-run-now-btn"
            title="Re-learn now"
            className="px-2 py-1 rounded-lg text-[10px] font-bold bg-purple-500/15 text-purple-200 border border-purple-500/30 hover:bg-purple-500/30 disabled:opacity-40 inline-flex items-center gap-1"
          >
            <ArrowsClockwise size={10} weight="bold" className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Learning…' : 'Re-learn now'}
          </button>
          {hasAny && (
            <button
              onClick={handleReset}
              disabled={busy}
              data-testid="learned-reset-btn"
              title="Reset all learning (admin)"
              className="px-2 py-1 rounded-lg text-[10px] font-bold bg-red-500/10 text-red-200 border border-red-500/30 hover:bg-red-500/25 disabled:opacity-40 inline-flex items-center gap-1"
            >
              <Trash size={10} weight="bold" /> Reset
            </button>
          )}
        </div>
      </div>

      {!hasAny ? (
        <p className="text-[11px] text-neutral-400 italic mt-1">
          The AI learns from what you actually cook, rate, skip, and swap. Once you have a few weeks of activity, this panel will fill up with actionable insights — and you&apos;ll be able to keep or forget each one.
        </p>
      ) : (
        <>
          {data.summary && (
            <div className="rounded-xl p-2.5 mt-1 text-[11px] text-neutral-300 whitespace-pre-line"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.05)' }}
              data-testid="learned-summary">
              {data.summary}
            </div>
          )}

          {applied.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <p className="text-[9px] uppercase tracking-wider font-bold text-emerald-300/80">Applied to your Food Profile</p>
              {applied.slice(0, expanded ? applied.length : 4).map(L => (
                <LearningRow key={L.learning_id} L={L} onFeedback={handleFeedback} busy={busy} />
              ))}
            </div>
          )}

          {observations.length > 0 && (
            <div className="mt-3 space-y-1.5">
              <p className="text-[9px] uppercase tracking-wider font-bold text-purple-300/80">Observations (not applied — thumbs up to enable)</p>
              {observations.slice(0, expanded ? observations.length : 3).map(L => (
                <LearningRow key={L.learning_id} L={L} onFeedback={handleFeedback} busy={busy} />
              ))}
            </div>
          )}

          {(applied.length > 4 || observations.length > 3 || (data.learnings || []).some(L => L.forgotten)) && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="mt-2 text-[10px] font-bold text-purple-300 hover:text-purple-200 underline"
              data-testid="learned-toggle-expand"
            >
              {expanded ? 'Show less' : `Show all ${(data.learnings || []).length} learnings`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
